import * as React from "react";
import type { VideoEmbedProps } from "@site-factory/config";
import { cn } from "../utils";

export interface VideoEmbedComponentProps extends VideoEmbedProps {
  className?: string;
}

export function VideoEmbed({
  provider,
  videoId,
  src,
  aspectRatio = "16/9",
  autoplay = false,
  controls = true,
  title = "Embedded video",
  className,
}: VideoEmbedComponentProps) {
  const ratioClass =
    aspectRatio === "4/3" ? "aspect-[4/3]" : aspectRatio === "1/1" ? "aspect-square" : "aspect-video";

  const embedSrc = React.useMemo(() => {
    if (provider === "youtube" && videoId) {
      const params = new URLSearchParams({
        rel: "0",
        modestbranding: "1",
        autoplay: autoplay ? "1" : "0",
        controls: controls ? "1" : "0",
      });
      return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
    }
    if (provider === "vimeo" && videoId) {
      const params = new URLSearchParams({
        autoplay: autoplay ? "1" : "0",
        title: "0",
        byline: "0",
        portrait: "0",
        controls: controls ? "1" : "0",
      });
      return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
    }
    return src || "";
  }, [provider, videoId, autoplay, controls, src]);

  if (!embedSrc) return null;

  return (
    <div className={cn("relative w-full overflow-hidden rounded-xl shadow-md bg-neutral-100", ratioClass, className)}>
      <iframe
        src={embedSrc}
        title={title}
        allow={autoplay ? "autoplay; fullscreen" : "fullscreen"}
        allowFullScreen
        loading={autoplay ? "eager" : "lazy"}
        className="absolute inset-0 h-full w-full border-0"
      />
    </div>
  );
}
